import { useMemo } from 'react';

import { useIdParam } from '@/react/hooks/useIdParam';
import { useUnauthorizedRedirect } from '@/react/hooks/useUnauthorizedRedirect';
import { PolicyOverrideAlert } from '@/react/portainer/policies/components/PolicyOverrideAlert';
import { PolicyType, TypedPolicy } from '@/react/portainer/policies/types';
import { usePolicy } from '@/react/portainer/policies/queries/usePolicy';

import { EnvironmentGroup } from '../../types';
import { useGroup } from '../../queries/useGroup';

import { CreateAccessWidget } from './CreateAccessWidget/CreateAccessWidget';
import { AccessDatatableWrapper } from './AccessDatatableWrapper/AccessDatatableWrapper';

function findRbacPolicy(group?: {
  Policies?: Array<{ Id: number; Name: string; Type: string }>;
}) {
  return group?.Policies?.find(
    (p) => p.Type === 'rbac-docker' || p.Type === 'rbac-k8s'
  );
}

interface RbacPolicyData {
  EnvironmentAccessPolicies?: {
    UserAccessPolicies?: Record<string, unknown>;
    TeamAccessPolicies?: Record<string, unknown>;
  };
}

function extractRoleId(entry: unknown): number {
  const obj = entry as Record<string, unknown>;
  if (
    'RoleId' in obj &&
    typeof obj.RoleId === 'object' &&
    obj.RoleId !== null
  ) {
    return (obj.RoleId as { value: number }).value;
  }
  if ('value' in obj && typeof obj.value === 'object' && obj.value !== null) {
    return (obj.value as { RoleId: number }).RoleId;
  }
  return 0;
}

function extractAccessPolicies(policy?: TypedPolicy<unknown>) {
  if (!policy?.Data) return { userPolicies: {}, teamPolicies: {} };

  const data = policy.Data as RbacPolicyData;
  const envAccess = data.EnvironmentAccessPolicies;
  if (!envAccess) return { userPolicies: {}, teamPolicies: {} };

  const userPolicies: Record<number, { RoleId: number }> = {};
  Object.entries(envAccess.UserAccessPolicies ?? {}).forEach(([id, entry]) => {
    userPolicies[Number(id)] = { RoleId: extractRoleId(entry) };
  });

  const teamPolicies: Record<number, { RoleId: number }> = {};
  Object.entries(envAccess.TeamAccessPolicies ?? {}).forEach(([id, entry]) => {
    teamPolicies[Number(id)] = { RoleId: extractRoleId(entry) };
  });

  return { userPolicies, teamPolicies };
}

export function AccessTab() {
  useUnauthorizedRedirect(
    { authorizations: ['EndpointResourcesAccess'], adminOnlyCE: true },
    { to: 'portainer.groups' }
  );

  const groupId = useIdParam();
  const groupQuery = useGroup(groupId);
  const group = groupQuery.data;
  const rbacPolicy = findRbacPolicy(group);

  const policyQuery = usePolicy(rbacPolicy?.Id ?? 0, {
    enabled: !!rbacPolicy,
  });

  const groupWithPolicyAccess = useMemo<EnvironmentGroup | undefined>(() => {
    if (!group) return undefined;
    if (!rbacPolicy || !policyQuery.data) return group;

    const { userPolicies, teamPolicies } = extractAccessPolicies(
      policyQuery.data
    );

    return {
      ...group,
      UserAccessPolicies: {
        ...group.UserAccessPolicies,
        ...userPolicies,
      },
      TeamAccessPolicies: {
        ...group.TeamAccessPolicies,
        ...teamPolicies,
      },
    };
  }, [group, rbacPolicy, policyQuery.data]);

  return (
    <>
      <div className="m-4">
        {rbacPolicy ? (
          <PolicyOverrideAlert
            policyName={rbacPolicy.Name}
            policyId={rbacPolicy.Id}
            policyType={rbacPolicy.Type as PolicyType}
          />
        ) : (
          <CreateAccessWidget
            group={group}
            onSuccess={() => groupQuery.refetch()}
            isLoading={groupQuery.isLoading}
          />
        )}
      </div>
      <AccessDatatableWrapper group={groupWithPolicyAccess} />
    </>
  );
}
